import { PageHeader, FilterBar, LoadingState, DataTable, ActionToolbar } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell, TableHead, TableRow,
  Grid, CircularProgress, Chip, MenuItem, Select, FormControl, InputLabel, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, FormControlLabel, Checkbox, Popover,
  Tabs, Tab, Container, Avatar, Stack, IconButton, Tooltip, useMediaQuery, alpha, useTheme,
  TablePagination, Alert
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
  Calculate as CalculateIcon,
  CheckCircle,
  Payments,
  Send,
  Close as CloseIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import api from '../utils/Api';
import { toast } from 'react-toastify';
import PayrollSettingsPage from './PayrollSettingsPage';

// Mobile-friendly teacher salary card
const MobileTeacherSalaryCard = ({ row, theme, handleEdit, handleOpenDeductionPopover, handleDownloadPdf }) => (
  <Card sx={{ mb: 2, boxShadow: 2 }}>
    <CardContent sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight="600">{row.teacher?.user?.name || 'Unknown'}</Typography>
          <Chip
            label={row.status}
            size="small"
            color={row.status === 'Paid' ? 'success' : row.status === 'Approved' ? 'primary' : row.status === 'Draft' ? 'default' : 'warning'}
            variant={row.status === 'Not Generated' ? 'outlined' : 'filled'}
          />
        </Stack>

        <Stack direction="row" justifyContent="space-between">
          <Box>
            <Typography variant="caption" color="text.secondary">Gross Pay</Typography>
            <Typography variant="body2" fontWeight="bold">GHS {row.displayGross}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Deductions</Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{ cursor: 'pointer', borderBottom: '1px dashed #ccc', display: 'inline-block' }}
              onClick={(e) => handleOpenDeductionPopover(e, row)}
            >
              GHS {row.displayDeductions}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Net Pay</Typography>
            <Typography variant="body2" fontWeight="bold" color="success.main">GHS {row.displayNet}</Typography>
          </Box>
        </Stack>

        <ActionToolbar direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            fullWidth
            onClick={() => handleEdit(row)}
          >
            Manage
          </Button>
          {row.isGenerated && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PdfIcon />}
              fullWidth
              onClick={() => handleDownloadPdf(row.payslip)}
            >
              PDF
            </Button>
          )}
        </ActionToolbar>
      </Stack>
    </CardContent>
  </Card>
);

export default function PayrollProcessingPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [history, setHistory] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [notificationFeedback, setNotificationFeedback] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Pagination for teacher table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog & Popover state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState(null);

  const [deductionAnchorEl, setDeductionAnchorEl] = useState(null);
  const [deductionTargetRow, setDeductionTargetRow] = useState(null);
  const [inlineDeductions, setInlineDeductions] = useState({});
  const [customName, setCustomName] = useState('');
  const [customAmount, setCustomAmount] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [historyRes, salariesRes, settingsRes] = await Promise.all([
        api.get('/payroll/history'),
        api.get('/payroll/salaries'),
        api.get('/payroll/settings')
      ]);
      setHistory(historyRes.data.payrolls || []);
      setSalaries(salariesRes.data.salaries || []);
      setSettings(settingsRes.data.settings || null);
    } catch (err) {
      toast.error('Failed to load payroll data');
    } finally { setLoading(false); }
  };

  const currentPeriod = `${year}-${String(month).padStart(2, '0')}`;
  const currentBatch = useMemo(() => history.find(p => p.month === currentPeriod), [history, currentPeriod]);

  const unifiedList = useMemo(() => {
    return salaries.map(salary => {
      const teacherId = salary.teacher?._id;
      let payslip = null;
      let status = 'Not Generated';
      if (currentBatch && currentBatch.payslips) {
        payslip = currentBatch.payslips.find(s => String(s.teacher) === String(teacherId));
        if (payslip) status = currentBatch.status;
      }
      const projectedGross = (salary.baseSalary || 0) + (salary.allowances || []).reduce((acc, cur) => acc + (cur.amount || 0), 0);
      const projectedDeductions = (salary.deductions || []).reduce((acc, cur) => acc + (cur.amount || 0), 0);
      const projectedNet = projectedGross - projectedDeductions;
      return {
        ...salary,
        isGenerated: !!payslip,
        status,
        displayGross: payslip ? payslip.grossSalary : projectedGross,
        displayDeductions: payslip ? payslip.totalDeductions : projectedDeductions,
        displayNet: payslip ? payslip.netSalary : projectedNet,
        payslip
      };
    });
  }, [salaries, currentBatch]);

  const handleGenerate = async (silent = false) => {
    try {
      setGenerating(true);
      await api.post('/payroll/generate', { month, year });
      if (!silent) toast.success(`Payroll generated for ${currentPeriod}`);
      const res = await api.get('/payroll/history');
      setHistory(res.data.payrolls || []);
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.message || 'Failed to generate payroll');
    } finally { setGenerating(false); }
  };

  const showNotificationResult = (data, batchId) => {
    const { notifications, notificationsError, message } = data;
    const channels = [notifications?.sms, notifications?.push, notifications?.inApp];
    const failed = notificationsError || channels.some(channel => channel?.failed > 0);
    const sent = channels.reduce((total, channel) => total + (channel?.sent || 0), 0);
    const pending = notifications?.details?.some(detail => detail.reason === 'in_progress');
    const severity = failed ? 'warning' : pending || sent === 0 ? 'info' : 'success';
    const text = message || 'Payment notification processing completed.';
    setNotificationFeedback({ batchId, severity, message: text });
    toast[severity](text);
  };

  const updateBatchStatus = async (status) => {
    if (!currentBatch || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      const res = await api.put(`/payroll/${currentBatch._id}/status`, { status });
      const { payroll, notifications, notificationsError, message } = res.data;
      if (payroll) setHistory(current => current.map(batch => batch._id === payroll._id ? payroll : batch));
      if (notifications || notificationsError) showNotificationResult(res.data, currentBatch._id);
      else toast.success(message || `Payroll marked as ${status}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not confirm payroll status. Please retry; successful notifications will be skipped.');
    } finally { setUpdatingStatus(false); }
  };

  // Inline deduction handlers
  const getDeductionKeys = () => {
    const keys = new Set();
    settings?.components?.filter(c => c.type === 'deduction').forEach(c => keys.add(c.name));
    keys.add('Late Penalty');
    keys.add('Absent Penalty');
    if (deductionTargetRow) {
      const salaryConfig = salaries.find(s => String(s.teacher?._id) === String(deductionTargetRow.teacher?._id));
      (salaryConfig?.deductions || []).forEach(d => keys.add(d.name));
    }
    return Array.from(keys);
  };

  const openDeductionPopover = (event, row) => {
    setDeductionAnchorEl(event.currentTarget);
    setDeductionTargetRow(row);
    const salaryConfig = salaries.find(s => String(s.teacher?._id) === String(row.teacher?._id)) || { deductions: [] };
    const initial = {};
    (salaryConfig.deductions || []).forEach(d => { initial[d.name] = d.amount; });
    setInlineDeductions(initial);
    setCustomName('');
    setCustomAmount('');
  };

  const closeDeductionPopover = () => {
    setDeductionAnchorEl(null);
    setDeductionTargetRow(null);
  };

  const submitInlineDeduction = async () => {
    if (!deductionTargetRow) return;
    try {
      const salaryConfig = salaries.find(s => String(s.teacher?._id) === String(deductionTargetRow.teacher?._id)) || {
        teacher: deductionTargetRow.teacher, baseSalary: 0, allowances: [], deductions: [], accountDetails: {}
      };
      const finalDeductions = [];
      Object.entries(inlineDeductions).forEach(([name, amount]) => {
        const num = Number(amount);
        if (num > 0) finalDeductions.push({ name, amount: num });
      });
      if (customName.trim() && Number(customAmount) > 0) {
        const existing = finalDeductions.find(d => d.name.toLowerCase() === customName.trim().toLowerCase());
        if (existing) existing.amount = Number(customAmount);
        else finalDeductions.push({ name: customName.trim(), amount: Number(customAmount) });
      }
      await api.post('/payroll/salaries', {
        teacherId: deductionTargetRow.teacher._id,
        employeeModel: deductionTargetRow.employeeModel || 'Teacher',
        baseSalary: salaryConfig.baseSalary || 0,
        allowances: salaryConfig.allowances || [],
        deductions: finalDeductions,
        accountDetails: salaryConfig.accountDetails || {}
      });
      closeDeductionPopover();
      if (currentBatch && currentBatch.status === 'Draft') {
        await handleGenerate(true);
        toast.success('Deductions applied instantly');
      } else {
        toast.success('Deductions updated');
      }
      fetchData();
    } catch (err) {
      toast.error('Failed to update inline deductions');
    }
  };

  // Salary configuration dialog handlers
  const handleEdit = (teacherUnifiedData) => {
    setSelectedSalary({
      ...teacherUnifiedData,
      accountDetails: teacherUnifiedData.accountDetails || {},
      allowances: teacherUnifiedData.allowances || [],
      deductions: teacherUnifiedData.deductions || []
    });
    setOpenDialog(true);
  };

  const handleSaveConfig = async () => {
    try {
      await api.post('/payroll/salaries', {
        teacherId: selectedSalary.teacher._id,
        employeeModel: selectedSalary.employeeModel || 'Teacher',
        baseSalary: selectedSalary.baseSalary,
        allowances: selectedSalary.allowances,
        deductions: selectedSalary.deductions,
        accountDetails: selectedSalary.accountDetails
      });
      toast.success('Salary structure updated');
      setOpenDialog(false);
      if (currentBatch && currentBatch.status === 'Draft') await handleGenerate(true);
      const res = await api.get('/payroll/salaries');
      setSalaries(res.data.salaries || []);
    } catch (err) {
      toast.error('Failed to update salary');
    }
  };

  const hasComponent = (compName, type) => {
    if (compName.toLowerCase() === 'basic salary') return selectedSalary.baseSalary > 0;
    const list = type === 'earning' ? selectedSalary.allowances : selectedSalary.deductions;
    return !!list.find(c => c.name === compName);
  };

  const toggleComponent = (comp) => {
    if (comp.name.toLowerCase() === 'basic salary') {
      setSelectedSalary(prev => ({
        ...prev,
        baseSalary: prev.baseSalary > 0 ? 0 : (comp.amount || 0)
      }));
      return;
    }
    const isEarning = comp.type === 'earning';
    const listKey = isEarning ? 'allowances' : 'deductions';
    const list = selectedSalary[listKey];
    if (hasComponent(comp.name, comp.type)) {
      setSelectedSalary(prev => ({ ...prev, [listKey]: list.filter(c => c.name !== comp.name) }));
    } else {
      setSelectedSalary(prev => ({ ...prev, [listKey]: [...list, { name: comp.name, amount: comp.amount }] }));
    }
  };

  const updateComponentAmount = (compName, type, val) => {
    if (compName.toLowerCase() === 'basic salary') {
      setSelectedSalary(prev => ({ ...prev, baseSalary: Number(val) }));
      return;
    }
    const isEarning = type === 'earning';
    const listKey = isEarning ? 'allowances' : 'deductions';
    setSelectedSalary(prev => ({
      ...prev,
      [listKey]: prev[listKey].map(c => c.name === compName ? { ...c, amount: Number(val) } : c)
    }));
  };

  // PDF generation moved to backend
  const downloadSinglePayslip = async (slip) => downloadPDFs(slip.teacher);
  const downloadBatchPayslips = async () => {
    if (currentBatch && currentBatch.payslips) downloadPDFs();
  };

  const downloadPDFs = async (teacherId = null) => {
    try {
      const url = teacherId 
        ? `/payroll/${currentBatch._id}/download-pdf?teacherId=${teacherId}`
        : `/payroll/${currentBatch._id}/download-pdf`;
        
      const res = await api.get(url, { responseType: 'blob' });
      
      const contentDisposition = res.headers['content-disposition'];
      let fileName = teacherId ? 'Payslip.pdf' : 'Batch_Payslips.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match && match.length === 2) fileName = match[1];
      }

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const linkHref = window.URL.createObjectURL(blob);
      const tempLink = document.createElement('a');
      tempLink.href = linkHref;
      tempLink.setAttribute('download', fileName);
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(linkHref);
    } catch (err) {
      toast.error('Failed to download payslips');
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Data for current page
  const paginatedUnifiedList = unifiedList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
        <LoadingState />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header */}
      <PageHeader title={<>Payroll Management</>} description={<>Process teacher and non-teaching staff salaries, payslips, and settings</>} actions={<>
        <Button component={Link} to="/dashboard/payroll/salaries" variant="outlined">Manage Staff & Salaries</Button>
        <Button variant="contained" startIcon={<CalculateIcon />} onClick={handleGenerate} disabled={generating} fullWidth={isMobile}>
                    {generating ? <CircularProgress size={24} color="inherit" /> : currentBatch ? 'Recalculate' : 'Generate Batch'}
                  </Button>
      </>}></PageHeader>
      <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <SummaryMetric sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.primary.main}`, '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: 6 } }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 36, height: 36 }}><MoneyIcon fontSize="small" /></Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Gross</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    GHS {currentBatch ? currentBatch.totalGross : '—'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <SummaryMetric sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.info.main}`, '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: 6 } }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.info.main, width: 36, height: 36 }}><MoneyIcon fontSize="small" /></Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Deductions</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    GHS {currentBatch ? currentBatch.totalDeductions : '—'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <SummaryMetric sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.secondary.main}`, '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: 6 } }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 36, height: 36 }}><Payments fontSize="small" /></Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">Net Payout</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    GHS {currentBatch ? currentBatch.totalNet : '—'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <SummaryMetric sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.success.main}`, '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: 6 } }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.success.main, width: 36, height: 36 }}><CheckCircle fontSize="small" /></Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Chip
                    label={currentBatch ? currentBatch.status : 'Not Generated'}
                    size="small"
                    color={currentBatch?.status === 'Paid' ? 'success' : currentBatch?.status === 'Approved' ? 'primary' : 'default'}
                    variant={currentBatch ? 'filled' : 'outlined'}
                  />
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
      </SummaryStrip>
      <FilterBar>
<FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
                {[...Array(12)].map((_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</MenuItem>
                ))}
              </Select>
            </FormControl>
<TextField size="small" label="Year" type="number" value={year} onChange={(e) => setYear(e.target.value)} sx={{ width: 100 }} />
      </FilterBar>

      {/* Stats Cards */}
      

      {notificationFeedback && notificationFeedback.batchId === currentBatch?._id && (
        <Alert severity={notificationFeedback.severity} sx={{ mb: 2 }} onClose={() => setNotificationFeedback(null)}>
          {notificationFeedback.message}
        </Alert>
      )}

      {/* Batch Actions */}
      {currentBatch && (
        <ActionToolbar direction={isMobile ? "column" : "row"} spacing={2} sx={{ mb: 3 }}>
          {currentBatch.status === 'Draft' && (
            <Button variant="outlined" startIcon={<CheckCircle />} disabled={updatingStatus || sendingNotifications} onClick={() => updateBatchStatus('Approved')}>Approve Batch</Button>
          )}
          {currentBatch.status === 'Paid' && (
            <Button variant="outlined" startIcon={<Send />} disabled={sendingNotifications || updatingStatus} onClick={async () => {
              setSendingNotifications(true);
              try {
                const res = await api.post(`/payroll/${currentBatch._id}/payment-notifications`);
                showNotificationResult(res.data, currentBatch._id);
              } catch (err) { toast.error(err.response?.data?.message || 'Failed to send payment notifications'); }
              finally { setSendingNotifications(false); }
            }}>{sendingNotifications ? 'Sending Notifications…' : 'Retry Payment Notifications'}</Button>
          )}
          {currentBatch.status === 'Approved' && (
            <Button variant="outlined" startIcon={<Send />} color="success" disabled={updatingStatus || sendingNotifications} onClick={() => updateBatchStatus('Paid')}>{updatingStatus ? 'Marking Paid & Sending…' : 'Mark as Paid'}</Button>
          )}
          <Button variant="outlined" startIcon={<PdfIcon />} onClick={downloadBatchPayslips}>Download All Payslips</Button>
        </ActionToolbar>
      )}

      {/* Teacher Salary List */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Payroll Processing" />
          <Tab label="Payroll Settings" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Card sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Staff Salary Breakdown</Typography>
            <Stack direction="row" spacing={2}>
              {isMobile && (
                <FormControl size="small">
                  <InputLabel>Month</InputLabel>
                  <Select value={month} label="Month" onChange={e => setMonth(e.target.value)} sx={{ minWidth: 100 }}>
                    {[...Array(12)].map((_, i) => <MenuItem key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              <Button variant="contained" size="small" onClick={handleGenerate} disabled={generating}>
                {generating ? <CircularProgress size={20} /> : 'Regenerate'}
              </Button>
            </Stack>
          </Box>

          {isMobile ? (
            <Box sx={{ p: 2 }}>
              {paginatedUnifiedList.map(row => (
                <MobileTeacherSalaryCard
                  key={row.teacher?._id}
                  row={row}
                  theme={theme}
                  handleEdit={handleEdit}
                  handleOpenDeductionPopover={openDeductionPopover}
                  handleDownloadPdf={downloadSinglePayslip}
                />
              ))}
              {unifiedList.length === 0 && <Typography align="center" color="text.secondary">No salary data found</Typography>}
              <TablePagination
                component="div"
                count={unifiedList.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </Box>
          ) : (
            <>
              <Box sx={{ width: '100%', overflow: 'auto', maxHeight: isTablet ? '60vh' : 'none' }}>
                <DataTable><Table>
                  <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Staff Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Gross Pay</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Deductions</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Net Pay</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUnifiedList.map(row => (
                      <TableRow key={row.teacher?._id} sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}>
                        <TableCell>{row.teacher?.user?.name || 'Unknown'}</TableCell>
                        <TableCell>GHS {row.displayGross}</TableCell>
                        <TableCell>
                          <Box
                            sx={{ cursor: 'pointer', borderBottom: '1px dashed #ccc', display: 'inline-block', minWidth: 40 }}
                            onClick={(e) => openDeductionPopover(e, row)}
                            title="Click to edit deductions directly"
                          >
                            GHS {row.displayDeductions}
                          </Box>
                        </TableCell>
                        <TableCell>GHS {row.displayNet}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={row.status}
                            size="small"
                            color={row.status === 'Paid' ? 'success' : row.status === 'Approved' ? 'primary' : row.status === 'Draft' ? 'default' : 'warning'}
                            variant={row.status === 'Not Generated' ? 'outlined' : 'filled'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="Manage Salary">
                              <IconButton size="small" onClick={() => handleEdit(row)}><EditIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            {row.isGenerated && (
                              <Tooltip title="Download PDF">
                                <IconButton size="small" onClick={() => downloadSinglePayslip(row.payslip)}><PdfIcon fontSize="small" /></IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {unifiedList.length === 0 && (
                      <TableRow><TableCell colSpan={6} align="center">No salary data found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table></DataTable>
              </Box>
              <TablePagination
                component="div"
                count={unifiedList.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </Card>
      )}

      {tabValue === 1 && <PayrollSettingsPage />}

      {/* Salary Configuration Dialog (unchanged content, just restyled backdrop) */}
      {selectedSalary && (
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <Typography variant="h6" fontWeight="bold">Edit Salary - {selectedSalary.teacher?.user?.name}</Typography>
            <IconButton onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Earnings / Allowances</Typography>
                {settings?.components?.filter(c => c.type === 'earning').map((comp, idx) => {
                  const isActive = hasComponent(comp.name, comp.type);
                  let activeAmount = 0;
                  if (comp.name.toLowerCase() === 'basic salary') activeAmount = selectedSalary.baseSalary;
                  else if (isActive) activeAmount = selectedSalary.allowances.find(c => c.name === comp.name)?.amount || 0;
                  return (
                    <Box key={idx} display="flex" alignItems="center" mb={2} gap={1}>
                      <FormControlLabel
                        control={<Checkbox checked={isActive} onChange={() => toggleComponent(comp)} />}
                        label={comp.name}
                        sx={{ width: '50%' }}
                      />
                      {isActive && (
                        <TextField
                          size="small" type="number" label="Amount (GHS)"
                          value={activeAmount}
                          onChange={e => updateComponentAmount(comp.name, comp.type, e.target.value)}
                          sx={{ width: '40%' }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Deductions</Typography>
                {settings?.components?.filter(c => c.type === 'deduction').map((comp, idx) => {
                  const isActive = hasComponent(comp.name, comp.type);
                  const activeComp = isActive ? selectedSalary.deductions.find(c => c.name === comp.name) : null;
                  return (
                    <Box key={idx} display="flex" alignItems="center" mb={2} gap={1}>
                      <FormControlLabel
                        control={<Checkbox checked={isActive} onChange={() => toggleComponent(comp)} />}
                        label={comp.name}
                        sx={{ width: '50%' }}
                      />
                      {isActive && (
                        <TextField
                          size="small" type="number" label="Amount (GHS)"
                          value={activeComp?.amount || 0}
                          onChange={e => updateComponentAmount(comp.name, comp.type, e.target.value)}
                          sx={{ width: '40%' }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Grid>
              <Grid size={12}><Divider sx={{ my: 2 }} /><Typography variant="h6">Bank Details</Typography></Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField label="Bank Name" fullWidth value={selectedSalary.accountDetails?.bankName || ''} onChange={e => setSelectedSalary({ ...selectedSalary, accountDetails: { ...selectedSalary.accountDetails, bankName: e.target.value } })} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField label="Account Number" fullWidth value={selectedSalary.accountDetails?.accountNumber || ''} onChange={e => setSelectedSalary({ ...selectedSalary, accountDetails: { ...selectedSalary.accountDetails, accountNumber: e.target.value } })} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField label="SSNIT Number" fullWidth value={selectedSalary.accountDetails?.ssnitNumber || ''} onChange={e => setSelectedSalary({ ...selectedSalary, accountDetails: { ...selectedSalary.accountDetails, ssnitNumber: e.target.value } })} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: isMobile ? 2 : 3 }}>
            <Button onClick={() => setOpenDialog(false)} variant="outlined">Cancel</Button>
            <Button variant="contained" onClick={handleSaveConfig}>Save Changes</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Inline Deduction Popover */}
      <Popover
        open={Boolean(deductionAnchorEl)}
        anchorEl={deductionAnchorEl}
        onClose={closeDeductionPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box p={2} sx={{ width: 300, maxHeight: 400, overflowY: 'auto' }}>
          <Typography variant="subtitle2" gutterBottom>Inline Deductions for {deductionTargetRow?.teacher?.user?.name}</Typography>
          {getDeductionKeys().map(key => (
            <TextField
              key={key}
              size="small"
              label={key}
              type="number"
              fullWidth
              value={inlineDeductions[key] || ''}
              onChange={e => setInlineDeductions({ ...inlineDeductions, [key]: e.target.value })}
              sx={{ mb: 1.5 }}
            />
          ))}
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="textSecondary" gutterBottom>Add Custom Deduction</Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid size={7}>
              <TextField size="small" label="Custom Name" fullWidth value={customName} onChange={e => setCustomName(e.target.value)} />
            </Grid>
            <Grid size={5}>
              <TextField size="small" label="Amount" type="number" fullWidth value={customAmount} onChange={e => setCustomAmount(e.target.value)} />
            </Grid>
          </Grid>
          <Button variant="contained" fullWidth onClick={submitInlineDeduction}>Save Deductions</Button>
        </Box>
      </Popover>
    </Container>
  );
}